import { memo, useEffect, useState } from 'react';
import type { GameId, Phase } from '@ringmaster/shared';
import { phaseLabel } from '../../ui/GameHeader.js';
import { SAFE } from '../BroadcastFrame.js';

/**
 * StageHUD — the top chrome of the Stage: phase label, round, and a large
 * broadcast-scale countdown. Reuses phaseLabel() from the Hand GameHeader so the
 * Stage and phones name every phase identically.
 *
 * The timer is authored at the --stage-text-title scale and turns urgent (blood,
 * pulsing) in the final 30 seconds — mirroring GameHeader's countdown mechanics
 * (250ms tick, tabular-nums, freeze-on-pause) but at TV scale. Everything derives
 * from primitive props (phase / round / timerEndsAt / paused / remaining), so a
 * zustand caller can primitive-select and never pass a fresh object.
 */

const URGENT_MS = 30_000;

export interface StageHUDProps {
  phase: Phase;
  round: number;
  /**
   * Which game's copy to label with. Optional (undefined → Blood Moon), but pass
   * it: the shared DEAL/LOBBY phases read differently per game — One Night's
   * opening beat is "Preparation", not "Day 1 - Preparation" (it has one night
   * and no day count), and The Whisper's is "The séance opens".
   */
  gameId?: GameId;
  /** Epoch-ms deadline for the current phase, or null when untimed. */
  timerEndsAt: number | null;
  paused?: boolean;
  /** Frozen ms remaining while paused. */
  timerRemainingMs?: number | null;
}

function fmt(msLeft: number): string {
  const total = Math.max(0, Math.ceil(msLeft / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

function StageHUDImpl({
  phase,
  round,
  gameId,
  timerEndsAt,
  paused = false,
  timerRemainingMs = null,
}: StageHUDProps) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (timerEndsAt == null || paused) return;
    const id = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(id);
  }, [timerEndsAt, paused]);

  const label = phaseLabel(phase, round, gameId);
  const frozenLeft = paused ? timerRemainingMs : null;
  const hasTimer = frozenLeft != null || timerEndsAt != null;
  const left = frozenLeft ?? (timerEndsAt != null ? timerEndsAt - now : 0);
  const urgent = !paused && left > 0 && left <= URGENT_MS;

  return (
    <div
      style={{
        position: 'absolute',
        top: SAFE,
        left: SAFE,
        right: SAFE,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 8,
      }}
    >
      {/* Ink scrim — a soft radial darkening behind the label + timer so the HUD
          stays legible wherever it overlaps a bright element (notably the NIGHT hero
          moon, where a red urgent timer over the red moon was unreadable). Purely
          cosmetic, non-interactive, sits behind the text. Sized to cover the timer's
          full footprint with a wide feathered edge so it never reads as a hard box. */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          top: -40,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 720,
          height: 280,
          background:
            'radial-gradient(58% 54% at 50% 44%, rgba(4,3,4,0.9) 0%, rgba(4,3,4,0.82) 26%, rgba(4,3,4,0.55) 54%, rgba(4,3,4,0) 80%)',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />

      <div
        style={{
          position: 'relative',
          zIndex: 1,
          fontFamily: 'var(--font-ui, Inter, sans-serif)',
          fontSize: 'var(--stage-text-meta, 24px)',
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: 'var(--text-secondary, #ababab)',
        }}
      >
        {label}
      </div>

      {hasTimer && (
        <div
          style={{
            position: 'relative',
            zIndex: 1,
            fontFamily: 'var(--font-ui, Inter, sans-serif)',
            fontSize: 'var(--stage-text-title, 96px)',
            fontWeight: 700,
            lineHeight: 1,
            fontVariantNumeric: 'tabular-nums',
            color: paused
              ? 'var(--text-tertiary, rgba(255,255,255,0.5))'
              : urgent
                ? 'var(--blood-6, #ff5a4f)'
                : 'var(--text-primary, #fff)',
            textShadow: urgent ? 'var(--rim-blood, 0 0 24px rgba(153,0,0,.55))' : 'none',
            animation: urgent ? 'stagehud-urgent 1s ease-in-out infinite' : 'none',
            transition: 'color var(--duration-base, 280ms) var(--ease-standard)',
          }}
        >
          {fmt(left)}
        </div>
      )}

      {paused && (
        <div
          style={{
            fontFamily: 'var(--font-ui, Inter, sans-serif)',
            fontSize: 16,
            fontWeight: 600,
            letterSpacing: '0.16em',
            textTransform: 'uppercase',
            color: 'var(--gold-5, #e6b866)',
            border: '1px solid var(--gold-3, #a9762f)',
            borderRadius: 'var(--radius-pill, 48px)',
            padding: '4px 16px',
          }}
        >
          Paused
        </div>
      )}

      {/* Gold divider */}
      <div
        aria-hidden
        style={{
          position: 'relative',
          zIndex: 1,
          marginTop: 6,
          width: 96,
          height: 2,
          background:
            'linear-gradient(90deg, rgba(230,184,102,0) 0%, rgba(230,184,102,0.9) 50%, rgba(230,184,102,0) 100%)',
        }}
      />

      <style>{`
        @keyframes stagehud-urgent {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%      { opacity: 0.72; transform: scale(1.04); }
        }
        @media (prefers-reduced-motion: reduce) {
          @keyframes stagehud-urgent { 0%, 100% { opacity: 1; transform: none; } }
        }
      `}</style>
    </div>
  );
}

export const StageHUD = memo(StageHUDImpl);
